import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Textarea } from '@/app/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { useCreateProject, useClients } from '@/hooks/use-projects';
import { useOrgCurrency, useOrganizationSettings } from '@/hooks/use-settings';
import { useAllCFLocations, useCreateCFLocation } from '@/hooks/use-cf-locations';
import { useZones } from '@/hooks/use-zones';
import { useSpots } from '@/hooks/use-spots';
import { ZonePicker } from '@/components/shared/zone-picker';
import { AddClientDialog } from '@/components/shared/add-client-dialog';
import { DepartmentSelect } from '@/components/shared/department-select';
import { useIndustry } from '@/hooks/use-industry';
import { handleApiError } from '@/lib/api-client';
import type {
  BillRateUnit,
  CFLocationType,
  CreateProjectPayload,
  Industry,
  ProjectClient,
  ProjectBillingType,
  ProjectPriority,
  ProjectStatus,
} from '@/lib/types';

const LOCATION_TYPE_DEFAULTS: Record<Industry, CFLocationType> = {
  field_ops: 'job_site',
  facility_ops: 'client_facility',
  training_ops: 'training_venue',
  consultant: 'client_facility',
  hybrid: 'job_site',
};

const BILLING_TYPES: { value: ProjectBillingType; label: string }[] = [
  { value: 'fixed_price', label: 'Fixed Price' },
  { value: 'time_and_materials', label: 'Time and Materials' },
  { value: 'cost_plus', label: 'Cost Plus' },
  { value: 'per_diem', label: 'Per Diem' },
];

const PRIORITY_OPTIONS: { value: ProjectPriority; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

interface ProjectForm {
  name: string;
  client?: number;
  client_name: string;
  contact_name: string;
  phone: string;
  description: string;
  status: ProjectStatus;
  priority: ProjectPriority;
  billing_type: ProjectBillingType;
  start_date: string;
  end_date: string;
  location_fk?: number;
  zone?: number;
  spot?: number;
  budget: string;
  billing_rate: string;
  billing_rate_unit: BillRateUnit;
  po_number: string;
  contract_value: string;
  retainage_percent: string;
  department?: number;
}

export function ProjectCreatePage() {
  const navigate = useNavigate();
  const createProject = useCreateProject();
  const { data: orgSettings, isLoading: settingsLoading } = useOrganizationSettings();
  const currency = useOrgCurrency();
  const projectsEnabled = orgSettings?.modules.projects ?? false;
  // Clients are foundation records — readable in every module combination.
  const { data: clients } = useClients({ page_size: 100, is_active: true }, true);
  const { data: cfLocations } = useAllCFLocations();
  const createCFLocation = useCreateCFLocation();
  const { labels, industry } = useIndustry();

  const [form, setForm] = useState<ProjectForm>({
    name: '',
    client: undefined,
    client_name: '',
    contact_name: '',
    phone: '',
    description: '',
    status: 'draft',
    priority: 'medium',
    billing_type: 'time_and_materials',
    start_date: '',
    end_date: '',
    location_fk: undefined,
    zone: undefined,
    spot: undefined,
    budget: '',
    billing_rate: '',
    billing_rate_unit: 'hourly',
    po_number: '',
    contract_value: '',
    retainage_percent: '',
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [createdClient, setCreatedClient] = useState<ProjectClient | null>(null);
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [newLocationName, setNewLocationName] = useState('');
  const [showNewLocation, setShowNewLocation] = useState(false);
  const newLocationCreateButtonRef = useRef<HTMLButtonElement>(null);
  const clientOptions = createdClient
    ? [
        createdClient,
        ...(clients?.results ?? []).filter((client) => client.id !== createdClient.id),
      ]
    : clients?.results ?? [];

  const { data: zonesData } = useZones(
    { location: form.location_fk, page_size: 1000 },
    !!form.location_fk,
  );
  const { data: spotsData } = useSpots(
    { location: form.location_fk, zone: form.zone, page_size: 200 },
    !!form.location_fk,
  );

  const zones = zonesData?.results ?? [];
  const spots = spotsData?.results ?? [];

  const updateField = (field: keyof ProjectForm, value: string | number | undefined) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleLocationChange = (val: string) => {
    if (val === 'new') {
      setShowNewLocation(true);
      return;
    }
    const locationId = val === 'none' ? undefined : parseInt(val);
    setForm((prev) => ({ ...prev, location_fk: locationId, zone: undefined, spot: undefined }));
  };

  const handleZoneChange = (zoneId: number | null) => {
    setForm((prev) => ({ ...prev, zone: zoneId ?? undefined, spot: undefined }));
  };

  const handleSpotChange = (val: string) => {
    const spotId = val === 'none' ? undefined : parseInt(val);
    setForm((prev) => ({ ...prev, spot: spotId }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    try {
      // Basic-job fields are free in every module combination; only the
      // H3Books fields below stay gated on the projects module.
      const payload: CreateProjectPayload = {
        name: form.name.trim(),
        location_fk: form.location_fk ?? undefined,
        zone: form.zone ?? undefined,
        spot: form.spot ?? undefined,
        client: form.client || undefined,
        client_name: form.client_name.trim() || undefined,
        contact_name: form.contact_name.trim() || undefined,
        phone: form.phone.trim() || undefined,
        description: form.description.trim() || undefined,
        status: form.status,
        priority: form.priority,
        start_date: form.start_date || undefined,
        end_date: form.end_date || undefined,
      };

      if (projectsEnabled) {
        payload.budget = form.budget || undefined;
        payload.billing_type = form.billing_type;
        payload.billing_rate = form.billing_rate || undefined;
        payload.billing_rate_unit = form.billing_rate_unit;
        payload.po_number = form.po_number.trim() || undefined;
        payload.contract_value = form.contract_value.trim() || undefined;
        payload.retainage_percent = form.retainage_percent.trim() || undefined;
        payload.department = form.department || undefined;
      }

      const project = await createProject.mutateAsync(payload);
      navigate(`/projects/${project.id}`);
    } catch (err) {
      const apiError = handleApiError(err);
      const fieldMessages = Object.entries(apiError.errors ?? {})
        .map(([field, messages]) => `${field}: ${messages.join(' ')}`)
        .join('; ');
      setFormError(fieldMessages || apiError.message);
    }
  };

  if (settingsLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-3xl">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <h1 className="text-2xl font-bold text-gray-900">New Project</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Client Selection */}
        <Card>
          <CardHeader>
            <CardTitle>{labels.client}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Select {labels.client}</Label>
              <Select
                value={form.client?.toString() ?? 'none'}
                onValueChange={(val) =>
                  updateField('client', val === 'none' ? undefined : parseInt(val))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder={`Select a ${labels.client.toLowerCase()} (optional)`} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No {labels.client}</SelectItem>
                  {clientOptions.map((c) => (
                    <SelectItem key={c.id} value={c.id.toString()}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                <button
                  type="button"
                  data-skip-required-validation="true"
                  className="text-blue-600 hover:underline"
                  onClick={() => setClientDialogOpen(true)}
                >
                  + Create new {labels.client.toLowerCase()}
                </button>
              </p>
            </div>
            {!projectsEnabled && (
              <>
                <div>
                  <Label htmlFor="client_name">Client Name</Label>
                  <Input
                    id="client_name"
                    value={form.client_name}
                    onChange={(e) => updateField('client_name', e.target.value)}
                    placeholder="Enter the client name"
                  />
                </div>
                <div>
                  <Label htmlFor="contact_name">Contact Name</Label>
                  <Input
                    id="contact_name"
                    value={form.contact_name}
                    onChange={(e) => updateField('contact_name', e.target.value)}
                    placeholder="Enter the contact name"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => updateField('phone', e.target.value)}
                    placeholder="Enter the phone number"
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Project Details */}
        <Card>
          <CardHeader>
            <CardTitle>Project Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="name">Project Name *</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => updateField('name', e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={form.description}
                onChange={(e) => updateField('description', e.target.value)}
                rows={3}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Priority</Label>
                <Select
                  value={form.priority}
                  onValueChange={(val) => updateField('priority', val)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITY_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {projectsEnabled && (
                <div>
                  <Label>Department *</Label>
                  <DepartmentSelect
                    value={form.department}
                    onChange={(department) => updateField('department', department)}
                    required
                  />
                </div>
              )}
              <div>
                <Label htmlFor="location_fk">{labels.location}</Label>
                <Select
                  value={form.location_fk?.toString() ?? 'none'}
                  onValueChange={handleLocationChange}
                >
                  <SelectTrigger id="location_fk">
                    <SelectValue placeholder={`Select a ${labels.location.toLowerCase()}`} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No {labels.location}</SelectItem>
                    {cfLocations?.map((loc) => (
                      <SelectItem key={loc.id} value={loc.id.toString()}>
                        {loc.name}
                      </SelectItem>
                    ))}
                    <SelectItem value="new">+ Create new {labels.location.toLowerCase()}</SelectItem>
                  </SelectContent>
                </Select>
                {showNewLocation && (
                  <div
                    id="new-project-location-fields"
                    data-required-validation-boundary="true"
                    className="flex gap-2 mt-2"
                  >
                    <Input
                      placeholder={`New ${labels.location.toLowerCase()} name`}
                      value={newLocationName}
                      onChange={(e) => setNewLocationName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key !== 'Enter') return;
                        e.preventDefault();
                        newLocationCreateButtonRef.current?.click();
                      }}
                      data-required="true"
                      data-required-empty={newLocationName.trim() ? 'false' : 'true'}
                      data-required-message={`${labels.location} name is required.`}
                    />
                    <Button
                      ref={newLocationCreateButtonRef}
                      type="button"
                      size="sm"
                      data-validate-required="true"
                      data-validation-scope="new-project-location-fields"
                      disabled={createCFLocation.isPending}
                      onClick={async () => {
                        try {
                          const loc = await createCFLocation.mutateAsync({
                            name: newLocationName.trim(),
                            type: LOCATION_TYPE_DEFAULTS[industry],
                          });
                          setForm((prev) => ({
                            ...prev,
                            location_fk: loc.id,
                            zone: undefined,
                            spot: undefined,
                          }));
                          setNewLocationName('');
                          setShowNewLocation(false);
                        } catch {
                          // error handled by mutation
                        }
                      }}
                    >
                      {createCFLocation.isPending ? 'Creating...' : 'Create'}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setShowNewLocation(false);
                        setNewLocationName('');
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
            </div>
            {form.location_fk != null && (zones.length > 0 || spots.length > 0) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {zones.length > 0 && (
                  <div>
                    <Label>{labels.zone}</Label>
                    <ZonePicker
                      locationId={form.location_fk}
                      value={form.zone ?? null}
                      onChange={handleZoneChange}
                      placeholder={`Select a ${labels.zone.toLowerCase()}`}
                    />
                  </div>
                )}
                {spots.length > 0 && (
                  <div>
                    <Label>{labels.spot}</Label>
                    <Select
                      value={form.spot?.toString() ?? 'none'}
                      onValueChange={handleSpotChange}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={`Select a ${labels.spot.toLowerCase()}`} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No {labels.spot}</SelectItem>
                        {spots.map((s) => (
                          <SelectItem key={s.id} value={s.id.toString()}>
                            {s.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="start_date">Start Date</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={form.start_date}
                  onChange={(e) => updateField('start_date', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="end_date">End Date</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={form.end_date}
                  onChange={(e) => updateField('end_date', e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Billing Configuration */}
        {projectsEnabled && (
          <Card>
            <CardHeader>
              <CardTitle>Billing Configuration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Billing Type</Label>
                  <Select
                    value={form.billing_type}
                    onValueChange={(val) => updateField('billing_type', val)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BILLING_TYPES.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="po_number">PO Number</Label>
                  <Input
                    id="po_number"
                    value={form.po_number}
                    onChange={(e) => updateField('po_number', e.target.value)}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="budget">Budget ({currency})</Label>
                  <Input
                    id="budget"
                    type="number"
                    step="0.01"
                    value={form.budget}
                    onChange={(e) => updateField('budget', e.target.value)}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <Label htmlFor="billing_rate">Billing Rate ({currency})</Label>
                  <div className="flex gap-2">
                    <Input
                      id="billing_rate"
                      type="number"
                      step="0.01"
                      value={form.billing_rate}
                      onChange={(e) => updateField('billing_rate', e.target.value)}
                      placeholder="0.00"
                    />
                    <Select
                      value={form.billing_rate_unit ?? 'hourly'}
                      onValueChange={(value) =>
                        updateField('billing_rate_unit', value as BillRateUnit)
                      }
                    >
                      <SelectTrigger className="w-28">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hourly">Per hour</SelectItem>
                        <SelectItem value="daily">Per day</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contract_value">Contract Value ({currency})</Label>
                  <Input
                    id="contract_value"
                    type="number"
                    step="0.01"
                    value={form.contract_value}
                    onChange={(e) => updateField('contract_value', e.target.value)}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <Label htmlFor="retainage_percent">Retainage (%)</Label>
                  <Input
                    id="retainage_percent"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={form.retainage_percent}
                    onChange={(e) => updateField('retainage_percent', e.target.value)}
                    placeholder="0.00"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {formError && (
          <div className="text-sm text-red-600 bg-red-50 p-3 rounded" role="alert">
            {formError}
          </div>
        )}

        <div className="flex gap-3 justify-end">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button type="submit" disabled={createProject.isPending}>
            {createProject.isPending ? 'Creating...' : 'Create Project'}
          </Button>
        </div>
      </form>
      <AddClientDialog
        open={clientDialogOpen}
        onOpenChange={setClientDialogOpen}
        onCreated={(client) => {
          setCreatedClient(client);
          setForm((previous) => ({
            ...previous,
            client: client.id,
            client_name: client.name,
          }));
        }}
      />
    </div>
  );
}
